import ByteChunksView from '../byteChunksView';
import { byteChunksToInt, numberToBytes } from '../utils';
import PngChunkView from './pngChunkView';

export enum ColorType
    {
    GREYSCALE = 0,
    TRUE_COLOR = 2,
    INDEXED = 3,
    GREYSCALE_WITH_ALPHA = 4,
    TRUE_COLOR_WITH_ALPHA = 6,
}

export enum Interlacing
    {
    STANDARD = 0,
    ADAM7 = 1,
}

const allowedBitDepthMappings: { [K in ColorType]: Set<number> } = {
    [ColorType.GREYSCALE]: new Set<number>([1, 2, 4, 8, 16]),
    [ColorType.TRUE_COLOR]: new Set<number>([8, 16]),
    [ColorType.INDEXED]: new Set<number>([1, 2, 4, 8]),
    [ColorType.GREYSCALE_WITH_ALPHA]: new Set<number>([8, 16]),
    [ColorType.TRUE_COLOR_WITH_ALPHA]: new Set<number>([8, 16]),
};

export default class ImageHeaderChunkView extends PngChunkView
{
    static readonly LENGTH = 13;

    private readonly rawWidth: ByteChunksView;
    private readonly rawHeight: ByteChunksView;
    private readonly rawDepth: ByteChunksView;
    private readonly rawFilter: ByteChunksView;
    private readonly rawCompression: ByteChunksView;
    private readonly rawColorType: ByteChunksView;
    private readonly rawInterlace: ByteChunksView;

    constructor(rawData: Uint8Array[])
    {
        super(rawData, 13);

        let tail = this.rawContent;

        [this.rawWidth, tail] = tail.split(4);
        [this.rawHeight, tail] = tail.split(4);
        [this.rawDepth, tail] = tail.split(1);
        [this.rawColorType, tail] = tail.split(1);
        [this.rawCompression, tail] = tail.split(1);
        [this.rawFilter, this.rawInterlace] = tail.split(1);

        if (this.compression !== 0)
        {
            throw new Error(`Unknown compression method ${this.compression}`);
        }

        if (this.filter !== 0)
        {
            throw new Error(`Unknown filtering method ${this.filter}`);
        }

        if (!(this.colorType in ColorType))
        {
            throw new Error(`Unknown color type ${this.colorType}`);
        }

        if (!allowedBitDepthMappings[this.colorType]?.has(this.depth))
        {
            throw new Error(
                `Bit depth ${this.depth} is not allowed for color type ${this.colorType}`,
            );
        }

        if (!(this.interlace in Interlacing))
        {
            throw new Error(`Unknown interlacing method ${this.interlace}`);
        }
    }

    get width(): number
    {
        return byteChunksToInt(this.rawWidth.getRawChunks());
    }

    set width(value)
    {
        const bytes = numberToBytes(value, 4);

        this.rawWidth.setBytes(bytes);
    }

    get height(): number
    {
        return byteChunksToInt(this.rawHeight.getRawChunks());
    }

    set height(value)
    {
        const bytes = numberToBytes(value, 4);

        this.rawHeight.setBytes(bytes);
    }

    get compression(): number
    {
        return byteChunksToInt(this.rawCompression.getRawChunks());
    }

    get filter(): number
    {
        return byteChunksToInt(this.rawFilter.getRawChunks());
    }

    get depth(): number
    {
        return byteChunksToInt(this.rawDepth.getRawChunks());
    }

    get colorType(): ColorType
    {
        return byteChunksToInt(this.rawColorType.getRawChunks());
    }

    get interlace(): Interlacing
    {
        return byteChunksToInt(this.rawInterlace.getRawChunks());
    }
}
